import React from "react";
import { Box, Typography } from "@mui/material";
import GoogleIcon from "../../../../../icon/GoogleIcon";
import ChoiceOptionsEditor from "../../utils/ChoiceOptionsEditor";
import {
  SURVEY_TYPES,
  SINGLE_CHOICE_OPTIONS_SECTION_DEFAULTS,
  SINGLE_CHOICE_OPTIONS_SECTION_TEXTS,
  SINGLE_CHOICE_OPTIONS_SECTION_STYLES,
  NESTED_HIGHLIGHT_COLORS,
  SINGLE_CHOICE_CARD_STYLES
} from "../../constants/constantsSurveys";
import { COMPONENT_MAP } from "../../constants/surveyComponentMap";

import {
  MAX_NESTING_LEVEL,
  TYPE_LABEL_MAP,
  isSingleChoice,
  normalizeChildrenDisplayIndexes,
  getNestedAppearanceProps
} from "../../utils/choiceQuestionUtils";

export default function SingleChoiceOptionsSection({
  question,
  isConditionalParent,
  currentLevel,
  displayIndex,
  options,
  inputRefs,
  getInputValue,
  handleOptionChange,
  handleAddOption,
  handleKeyDown,
  onUpdate,
  onRequestDeleteOption,
  onOpenConditionalMenu,
  conditionalMenuOptionId,
  showValidation
}) {
  const handleUpdateChildQuestion = (optionId, childQuestionId, fields) => {
    const newOptions = options.map((option) => {
      if (option.id !== optionId) {
        return option;
      }

      return {
        ...option,
        childQuestions: (option.childQuestions || []).map((child) =>
          child.id === childQuestionId
            ? {
                ...child,
                ...fields
              }
            : child
        )
      };
    });

    onUpdate?.({
      options: normalizeChildrenDisplayIndexes(newOptions, displayIndex)
    });
  };

  const handleDeleteChildQuestion = (optionId, childQuestionId) => {
    const newOptions = options.map((option) => {
      if (option.id !== optionId) {
        return option;
      }

      return {
        ...option,
        childQuestions: (option.childQuestions || []).filter(
          (child) => child.id !== childQuestionId
        )
      };
    });

    onUpdate?.({
      options: normalizeChildrenDisplayIndexes(newOptions, displayIndex)
    });
  };

  const handleEnableConditionalForChild = (optionId, childQuestionId) => {
    const newOptions = options.map((option) => {
      if (option.id !== optionId) {
        return option;
      }

      return {
        ...option,
        childQuestions: (option.childQuestions || []).map((child) =>
          child.id === childQuestionId
            ? {
                ...child,
                isConditionalParent: true
              }
            : child
        )
      };
    });

    onUpdate?.({
      options: normalizeChildrenDisplayIndexes(newOptions, displayIndex)
    });
  };

  const renderChildQuestion = (option, childQuestion) => {
    const ChildComponent = COMPONENT_MAP[childQuestion.type];

    if (!ChildComponent) {
      return null;
    }

    const childLevel =
      childQuestion.level || SINGLE_CHOICE_OPTIONS_SECTION_DEFAULTS.childLevel;
    const childCanAddConditional =
      isSingleChoice(childQuestion.type) && childLevel < MAX_NESTING_LEVEL;

    return (
      <Box
        key={childQuestion.id}
        sx={SINGLE_CHOICE_OPTIONS_SECTION_STYLES.childQuestionWrapper}
      >
        <ChildComponent
          question={childQuestion}
          index={0}
          showValidation={showValidation}
          isNested
          hideDuplicate
          parentOptionLabel={option.label || ""}
          questionLabel={`Pregunta ${childQuestion.displayIndex}:`}
          dragHandleProps={{}}
          onDelete={() => handleDeleteChildQuestion(option.id, childQuestion.id)}
          onDuplicate={() => {}}
          onUpdate={(fields) =>
            handleUpdateChildQuestion(option.id, childQuestion.id, fields)
          }
          forceCanAddConditional={childCanAddConditional}
          externalEnableConditional={
            childCanAddConditional
              ? () => handleEnableConditionalForChild(option.id, childQuestion.id)
              : null
          }
          {...getNestedAppearanceProps(childQuestion)}
        />
      </Box>
    );
  };

  const renderOptionActions = (option) => {
    const optionHasChildren = (option.childQuestions || []).length > 0;
    const childType = option.childQuestions?.[0]?.type;
    const conditionalSelectorLabel = optionHasChildren
      ? TYPE_LABEL_MAP[childType] ||
        SINGLE_CHOICE_OPTIONS_SECTION_TEXTS.conditionalSelectorDefaultLabel
      : SINGLE_CHOICE_OPTIONS_SECTION_TEXTS.conditionalSelectorDefaultLabel;

    if (!(isConditionalParent && currentLevel < MAX_NESTING_LEVEL)) {
      return null;
    }

    return (
      <Box
        onClick={(event) => onOpenConditionalMenu(event, option.id, false)}
        sx={{
          ...SINGLE_CHOICE_OPTIONS_SECTION_STYLES.conditionalSelector,
          ...(optionHasChildren
            ? SINGLE_CHOICE_OPTIONS_SECTION_STYLES.conditionalSelectorActive
            : SINGLE_CHOICE_OPTIONS_SECTION_STYLES.conditionalSelectorInactive)
        }}
      >
        <Typography sx={SINGLE_CHOICE_OPTIONS_SECTION_STYLES.conditionalSelectorText}>
          {conditionalSelectorLabel}
        </Typography>

        <GoogleIcon
          name={
            conditionalMenuOptionId === option.id
              ? SINGLE_CHOICE_OPTIONS_SECTION_TEXTS.arrowUpIcon
              : SINGLE_CHOICE_OPTIONS_SECTION_TEXTS.arrowDownIcon
          }
          size={SINGLE_CHOICE_OPTIONS_SECTION_DEFAULTS.arrowIconSize}
          color="currentColor"
          sx={SINGLE_CHOICE_OPTIONS_SECTION_STYLES.conditionalSelectorIcon}
        />
      </Box>
    );
  };

  const renderChildren = (option) => {
    return (option.childQuestions || []).map((childQuestion) =>
      renderChildQuestion(option, childQuestion)
    );
  };

  return (
    <ChoiceOptionsEditor
      type={SURVEY_TYPES.singleChoice}
      options={options}
      shouldValidate={Boolean(question.shouldValidate)}
      inputRefs={inputRefs}
      getInputValue={getInputValue}
      onOptionChange={handleOptionChange}
      onOptionKeyDown={handleKeyDown}
      onAddOption={handleAddOption}
      onDeleteOption={onRequestDeleteOption}
      renderOptionActions={renderOptionActions}
      renderChildren={renderChildren}
    />
  );
}